import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Package, TriangleAlert, FileDown, Loader2, Eye, Trash2, ShoppingBag, Bookmark, BookmarkCheck, Youtube, Instagram, Facebook, Music2, ExternalLink, Copy, Check, Play, Maximize2, X, MessageCircle, Send, Flag, Share, Coins, Images, Presentation, Heart, DollarSign, FileText, KeyRound, ListChecks, Headphones, Users, Compass } from "lucide-react";
import api, { imageUrl, API } from "@/lib/api";
import StarRating from "@/components/StarRating";
import PrintableManual from "@/components/PrintableManual";
import CreatorUnlockModal from "@/components/CreatorUnlockModal";
import ExpertChat from "@/components/ExpertChat";
import AudienceBadge from "@/components/AudienceBadge";
import { savePdf } from "@/lib/share";
import { buildItemUrl } from "@/lib/shop";
import { useAuth } from "@/context/AuthContext";
import { getVisitorId } from "@/components/TrafficTracker";
import { toast } from "sonner";

// Map a source URL to a platform-specific button (label + brand color + icon).
function sourceMeta(url) {
  const u = (url || "").toLowerCase();
  if (u.includes("youtu")) return { label: "YouTube", color: "#ff0000", Icon: Youtube };
  if (u.includes("instagram")) return { label: "Instagram", color: "#c13584", Icon: Instagram };
  if (u.includes("tiktok")) return { label: "TikTok", color: "#111111", Icon: Music2 };
  if (u.includes("facebook") || u.includes("fb.watch")) return { label: "Facebook", color: "#1877f2", Icon: Facebook };
  return { label: "Source", color: "#4f7746", Icon: ExternalLink };
}

export default function CommunityManual() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshSavedCount, refresh } = useAuth();
  const [manual, setManual] = useState(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [err, setErr] = useState(false);
  const [myRating, setMyRating] = useState(0);
  const [avg, setAvg] = useState({ rating_avg: 0, rating_count: 0 });
  const [amazonTag, setAmazonTag] = useState("");
  const [saved, setSaved] = useState({});
  const [copied, setCopied] = useState({});
  const [items, setItems] = useState(null); // rich Amazon product picks (thumbnail + title)
  const [playing, setPlaying] = useState(false); // inline poster → play
  const [expanded, setExpanded] = useState(false); // windowed (modal) player
  const inlineVideoRef = useRef(null);
  const [nextInfo, setNextInfo] = useState(null); // { next_id, category, total }
  const touchRef = useRef(null);
  const [comments, setComments] = useState([]);
  const [mats, setMats] = useState(null);           // { bundle:{owned,need_login,for_sale,price_usd}, included:[...], is_owner, logged_in }
  const [matBusy, setMatBusy] = useState(null);      // asset currently downloading
  const [buyBusy, setBuyBusy] = useState(false);     // starting Stripe checkout
  const [guestKitSid, setGuestKitSid] = useState(null); // paid guest session — enables the download link
  const [kitZipBusy, setKitZipBusy] = useState(false);
  const downloadAllKit = async () => {
    setKitZipBusy(true);
    try {
      const res = await api.get(`/community/${id}/kit.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(manual?.title || "kit").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-kit.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't build the kit — please try again.");
    } finally {
      setKitZipBusy(false);
    }
  };
  const downloadGuestKit = (sid) => {
    const base = (api.defaults?.baseURL || "").replace(/\/$/, "");
    window.location.href = `${base}/community/${id}/kit.zip?session_id=${encodeURIComponent(sid)}`;
  };
  const [commentText, setCommentText] = useState("");
  const [posting, setPosting] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportCategory, setReportCategory] = useState("");
  const [reportNote, setReportNote] = useState("");
  const [reportBusy, setReportBusy] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [videoSaved, setVideoSaved] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [didIt, setDidIt] = useState({ count: 0, entries: [] });
  const [diOpen, setDiOpen] = useState(false);
  const [diText, setDiText] = useState("");
  const [diLink, setDiLink] = useState("");
  const [diPhoto, setDiPhoto] = useState("");   // base64 data URL
  const [diBusy, setDiBusy] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState(1);
  const [tipMsg, setTipMsg] = useState("");
  const [tipBusy, setTipBusy] = useState(false);
  const [tipAllowance, setTipAllowance] = useState(null); // { max, remaining, real }
  const [loveBusy, setLoveBusy] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);   // guest "Join Us" prompt before downloading

  const toggleLove = async () => {
    if (!user) { toast.error("Log in to show some Love ❤️"); navigate("/login"); return; }
    setLoveBusy(true);
    try {
      const { data } = await api.post(`/community/${id}/love`);
      setManual((m) => m ? { ...m, loves: data.count, i_loved: data.loved } : m);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update — try again");
    } finally {
      setLoveBusy(false);
    }
  };

  const openTip = async () => {
    if (!user) { toast.error("Log in to tip the creator"); navigate("/login"); return; }
    setTipAmount(1);
    setTipMsg("");
    setTipOpen(true);
    try {
      const { data } = await api.get("/me/tips/allowance");
      setTipAllowance(data);
      const maxDollars = Math.min(5, Math.floor((data.daily_remaining_cents ?? 500) / 100));
      setTipAmount(maxDollars >= 1 ? 1 : 0);
    } catch { setTipAllowance({ min_cents: 100, max_cents: 500, daily_remaining_cents: 5000, enabled: true }); }
  };

  const sendTip = async () => {
    if (tipAmount < 1) return;
    setTipBusy(true);
    try {
      const { data } = await api.post(`/community/${id}/tip`, {
        amount: tipAmount * 100,
        message: tipMsg.trim(),
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;  // pay by card → straight to the creator's Stripe account
        return;
      }
      toast.error("Couldn't start the tip — try again");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the tip — try again");
    } finally {
      setTipBusy(false);
    }
  };

  useEffect(() => {
    if (!user || !id) { setVideoSaved(false); return; }
    api.get("/saved-videos/ids").then(({ data }) => setVideoSaved((data.ids || []).includes(id))).catch(() => {});
  }, [user, id]);

  useEffect(() => {
    if (!id) return;
    api.get(`/community/${id}/materials`).then(({ data }) => setMats(data)).catch(() => setMats(null));
  }, [id, user]);

  // Return from Stripe Checkout — confirm payment, unlock + auto-download.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const kitSid = p.get("kit");
    if (kitSid) setGuestKitSid(kitSid);
    const sid = p.get("session_id");
    const purchased = p.get("purchased");
    const tipped = p.get("tipped");
    if (p.get("cancelled") || p.get("tip_cancelled")) {
      toast("Checkout cancelled — no charge was made.");
      window.history.replaceState({}, "", `/community/${id}`);
      return;
    }
    if (sid && tipped) {
      (async () => {
        try {
          const { data } = await api.get(`/marketplace/status/${sid}`);
          if (data.payment_status === "paid") {
            toast.success("Your tip went through — thank you for supporting the creator! 💚");
            setManual((m) => m ? { ...m, tips: { coins: (m.tips?.coins || 0), count: (m.tips?.count || 0) } } : m);
          } else {
            toast("Tip is still processing — it'll land in a moment.");
          }
        } catch { /* ignore */ }
        window.history.replaceState({}, "", `/community/${id}`);
      })();
      return;
    }
    if (sid && purchased) {
      (async () => {
        try {
          const { data } = await api.get(`/marketplace/status/${sid}`);
          if (data.payment_status === "paid") {
            toast.success("Purchase complete — the full kit is yours! 🎉");
            if (!user) {
              // Guest buyer: no account to attach downloads to — surface a one-time link and auto-grab it.
              setGuestKitSid(sid);
              setTimeout(() => downloadGuestKit(sid), 600);
            } else {
              await loadMaterials();
              if (refresh) refresh();
            }
          } else {
            toast("Payment is still processing — check back in a moment.");
          }
        } catch { /* ignore */ }
        window.history.replaceState({}, "", `/community/${id}${!user ? `?kit=${sid}` : ""}`);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  const toggleSaveVideo = async () => {
    if (!user) { toast.error("Log in to save videos"); navigate("/login"); return; }
    setSaveBusy(true);
    const next = !videoSaved;
    setVideoSaved(next);
    try {
      if (next) await api.post("/saved-videos", { manual_id: id });
      else await api.delete(`/saved-videos/${id}`);
      toast.success(next ? "Saved to your videos ✅" : "Removed from saved");
      refreshSavedCount?.();
    } catch (e) {
      setVideoSaved(!next);
      toast.error("Couldn't update — try again");
    } finally {
      setSaveBusy(false);
    }
  };

  const submitReport = async () => {
    if (!reportCategory) return;
    setReportBusy(true);
    try {
      await api.post(`/community/${id}/comments/report`, { category: reportCategory, note: reportNote.trim() });
      toast.success("Thanks — our team will review this shortly.");
      setReportOpen(false); setReportCategory(""); setReportNote("");
    } catch {
      toast.error("Couldn't send report — try again");
    } finally {
      setReportBusy(false);
    }
  };

  const didItVerb = (() => {
    const c = (manual?.category || "").toLowerCase();
    if (/food|cook|recipe|bak/.test(c)) return "I Made This";
    if (/repair|home|fix|automotive|tech/.test(c)) return "I Fixed This";
    if (/garden/.test(c)) return "I Grew This";
    if (/fitness|health|medical/.test(c)) return "I Did This";
    if (/craft|fashion|art|music/.test(c)) return "I Made This";
    return "I Did It";
  })();

  const onDiPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); return; }
    const r = new FileReader();
    r.onload = () => setDiPhoto(r.result);
    r.readAsDataURL(f);
  };

  const copyVideoLink = async () => {
    try { await navigator.clipboard.writeText(window.location.href); toast.success("Video link copied — paste it as your proof!"); }
    catch { toast.error("Couldn't copy — copy it from your address bar"); }
  };

  const pasteDidItLink = async () => {
    try {
      const t = (await navigator.clipboard.readText() || "").trim();
      if (t) { setDiLink(t); toast.success("Link pasted"); }
      else toast("Your clipboard is empty — copy a video link first", { icon: "📋" });
    } catch { toast.error("Couldn't read clipboard — paste the link manually"); }
  };

  const submitDidIt = async () => {
    if (!user) { toast.error("Log in to add your win"); navigate("/login"); return; }
    if (!diPhoto && !diLink.trim()) { toast.error("Add proof — a photo or a link to your version"); return; }
    setDiBusy(true);
    try {
      const { data } = await api.post(`/community/${id}/did-it`, { text: diText.trim(), link: diLink.trim(), photo_b64: diPhoto || null });
      setDidIt((s) => ({ count: s.count + 1, entries: [data, ...s.entries] }));
      setDiText(""); setDiLink(""); setDiPhoto(""); setDiOpen(false);
      toast.success("Nice work — added to the wall! 🎉");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post — try again");
    } finally {
      setDiBusy(false);
    }
  };

  const deleteDidIt = async (eid) => {
    try {
      await api.delete(`/community/${id}/did-it/${eid}`);
      setDidIt((s) => ({ count: Math.max(0, s.count - 1), entries: s.entries.filter((x) => x.id !== eid) }));
    } catch { toast.error("Couldn't remove"); }
  };


  const postComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    if (!user) { toast.error("Log in to join the conversation"); navigate("/login"); return; }
    setPosting(true);
    try {
      const { data } = await api.post(`/community/${id}/comments`, { text });
      setComments((cs) => [data, ...cs]);
      setCommentText("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post — try again");
    } finally {
      setPosting(false);
    }
  };

  const removeComment = async (cid) => {
    try {
      await api.delete(`/community/${id}/comments/${cid}`);
      setComments((cs) => cs.filter((c) => c.id !== cid));
    } catch {
      toast.error("Couldn't delete that comment");
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (isNaN(then)) return "";
    const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (s < 60) return "just now";
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    const w = Math.floor(d / 7);
    if (w < 5) return `${w}w ago`;
    const mo = Math.floor(d / 30);
    if (mo < 12) return `${mo}mo ago`;
    return `${Math.floor(d / 365)}y ago`;
  };

  const goNext = () => {
    if (!nextInfo?.next_id) return;
    navigate(`/community/${nextInfo.next_id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goPrev = () => {
    if (!nextInfo?.prev_id) return;
    navigate(`/community/${nextInfo.prev_id}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onTouchStart = (e) => {
    const t = e.touches[0];
    touchRef.current = { x: t.clientX, y: t.clientY };
  };
  const onTouchEnd = (e) => {
    if (!touchRef.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    touchRef.current = null;
    // Clearly-horizontal swipe → change video. Left = next, right = previous.
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx < 0) goNext(); else goPrev();
    }
  };

  const openExpanded = () => {
    // Pause the inline hero video so we never get two audio tracks at once.
    try { inlineVideoRef.current?.pause(); } catch { /* noop */ }
    setExpanded(true);
  };

  const onVideoError = (e) => {
    const code = e?.target?.error?.code;
    // Only bail out for a genuinely unplayable file; transient network/decode hiccups can recover,
    // so keep the player mounted and just nudge softly instead of pulling it away.
    if (code === 4) {
      toast.error("This video can't play in your browser — try the Download button or the source link.");
    } else {
      toast("Connection's slow — the video may take a moment to load…");
    }
  };

  const downloadGuide = () => {
    // If the admin uploaded a full guide PDF, serve that; otherwise fall back to the generated one.
    if (manual?.guide_pdf_id) {
      const base = process.env.REACT_APP_BACKEND_URL || "";
      const a = document.createElement("a");
      a.href = `${base}/api/manuals/${manual.id}/guide-pdf`;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }
    savePdf(manual);
  };

  const MAT_META = {
    pics: { label: "Step Photos", Icon: Images },
    pdf: { label: "PDF", Icon: FileDown },
    pptx: { label: "Keynote", Icon: Presentation },
    worksheet: { label: "Worksheet", Icon: FileText },
    answer_key: { label: "Answer Key", Icon: KeyRound },
    quiz: { label: "Quiz", Icon: ListChecks },
    audiobook: { label: "Audio Lesson", Icon: Headphones },
  };

  const loadMaterials = async () => {
    try {
      const { data } = await api.get(`/community/${id}/materials`);
      setMats(data);
    } catch {
      setMats(null);
    }
  };

  const downloadMaterial = async (asset) => {
    setMatBusy(asset);
    try {
      const res = await api.get(`/community/${id}/material/${asset}`, { responseType: "blob" });
      const ext = asset === "pptx" ? "pptx" : asset === "pics" ? "zip" : asset === "audiobook" ? "mp3" : "pdf";
      const slug = (manual?.title || "stepmystep").replace(/[^a-z0-9]+/gi, "-").toLowerCase().slice(0, 40);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `stepmystep-${slug}-${asset}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed — please try again.");
    } finally {
      setMatBusy(null);
    }
  };

  // Real-money marketplace purchase of the full kit → redirect to Stripe Checkout.
  const buyKit = async () => {
    setBuyBusy(true);
    try {
      const { data } = await api.post(`/community/${id}/marketplace/checkout`, { origin_url: window.location.origin });
      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
      setBuyBusy(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start checkout — please try again.");
      setBuyBusy(false);
    }
  };

  // A single downloadable chip (used once the viewer owns the kit).
  const renderKitChip = (item) => {
    const meta = MAT_META[item.asset] || { label: item.label, Icon: FileDown };
    const Icon = meta.Icon;
    if (!item.available) {
      return (
        <div key={item.asset} data-testid={`kit-chip-${item.asset}-soon`} title={`${meta.label} — coming soon for this video`}
          className="flex items-center justify-center gap-1 font-bold rounded-full px-2.5 py-1.5 text-xs bg-[#e3e8dd] text-[#9aa691] cursor-not-allowed select-none">
          <Icon size={13} /> {meta.label}
        </div>
      );
    }
    return (
      <button key={item.asset} data-testid={`kit-chip-${item.asset}`} onClick={() => downloadMaterial(item.asset)}
        disabled={matBusy === item.asset} title={`Download ${meta.label}`}
        className="flex items-center justify-center gap-1 font-bold rounded-full px-2.5 py-1.5 text-white text-xs bg-[#4f7746] hover:bg-[#3f6238] transition-colors disabled:opacity-60">
        {matBusy === item.asset ? <Loader2 size={13} className="spin" /> : <Icon size={13} />} {meta.label}
      </button>
    );
  };

  const shareManual = async () => {
    const sid = manual?.id || id;
    const url = sid ? `${window.location.origin}/api/v/${sid}` : window.location.href;
    const title = manual?.title || "Vittyyo video";
    try {
      if (navigator.share) { await navigator.share({ title, url }); return; }
    } catch (e) { if (e?.name === "AbortError") return; }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied — share it anywhere!"); }
    catch { toast.error("Couldn't copy the link"); }
  };


  const copyLink = async (it, i) => {
    const url = it.url || buildItemUrl("Amazon", it.search_query || it.product_name, amazonTag);
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
    }
    setCopied((p) => ({ ...p, [i]: true }));
    toast.success("Amazon link copied — ready to paste");
    setTimeout(() => setCopied((p) => ({ ...p, [i]: false })), 2000);
  };

  const deleteItem = async (i) => {
    const prev = items;
    setItems((arr) => (arr || []).filter((_, j) => j !== i));
    try {
      await api.delete(`/manuals/${id}/shopping/${i}`);
      toast.success("Amazon link removed");
    } catch {
      setItems(prev);
      toast.error("Couldn't remove the link");
    }
  };

  useEffect(() => {
    api.get(`/community/${id}`, { params: { vid: getVisitorId(), count: 1 } }).then(({ data }) => {
      setManual(data);
      setAmazonTag(data.amazon_affiliate_tag || "");
      setAvg({ rating_avg: data.rating_avg || 0, rating_count: data.rating_count || 0 });
    }).catch(() => setErr(true));
    api.get("/config").then(({ data }) => setAmazonTag((prev) => prev || (data.amazon_affiliate_tag || ""))).catch(() => {});
    api.post(`/community/${id}/shopping`).then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
    if (user) api.get(`/manuals/${id}/rating`).then(({ data }) => setMyRating(data.my_rating || 0)).catch(() => {});
    api.get(`/community/${id}/next`).then(({ data }) => setNextInfo(data)).catch(() => setNextInfo(null));
    api.get(`/community/${id}/comments`).then(({ data }) => setComments(data.comments || [])).catch(() => setComments([]));
    api.get(`/community/${id}/did-it`).then(({ data }) => setDidIt(data || { count: 0, entries: [] })).catch(() => {});
  }, [id, user]);

  const saveItem = async (it, i) => {
    if (!user) {
      toast.info("Log in to save items");
      navigate("/login");
      return;
    }
    if (saved[i]) return;
    setSaved((p) => ({ ...p, [i]: true }));
    try {
      await api.post("/saved-items", {
        product_name: it.product_name, supply: it.supply, description: it.description,
        price_range: it.price_range, search_query: it.search_query || it.product_name,
        store: "Amazon", category: manual?.share_category || "General",
        manual_id: id, manual_title: manual?.title,
      });
      toast.success("Saved to your Buy Later list");
      refreshSavedCount?.();
    } catch {
      setSaved((p) => ({ ...p, [i]: false }));
      toast.error("Couldn't save that item");
    }
  };

  const rate = async (stars) => {
    if (!user) {
      toast.info("Log in to rate this manual");
      navigate("/login");
      return;
    }
    setMyRating(stars);
    try {
      const { data } = await api.post(`/community/${id}/rate`, { stars });
      setAvg({ rating_avg: data.rating_avg, rating_count: data.rating_count });
      toast.success("Thanks for rating!");
    } catch {
      toast.error("Couldn't submit rating");
    }
  };

  if (err) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 text-[#6b6880]">
        <p>This manual isn't available.</p>
        <button onClick={() => navigate("/")} className="sms-solid-btn text-white font-bold px-5 py-2.5 rounded-xl">Back to Community</button>
      </div>
    );
  }
  if (!manual) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 size={28} className="spin text-[#5d8a52]" /></div>;

  return (
    <div className="max-w-2xl mx-auto px-4 pb-16" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="relative flex items-center justify-between py-4">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-white font-bold hover:text-white/80 transition-colors" data-testid="back-to-community-btn">
          <ArrowLeft size={18} /> <span className="sm:hidden">Videos</span><span className="hidden sm:inline">{manual?.format === "podcast" ? "Podcast" : "Community"}</span>
        </button>
        {manual.avatar && (manual.avatar.emoji || manual.avatar.tag) && (
          <button
            onClick={() => {
              if (manual.creator_has_page && manual.creator_username) navigate(`/creator/${manual.creator_username}`);
              else if (user && manual.creator_username && user.username === manual.creator_username) setUnlockOpen(true);
              else toast("This creator hasn't unlocked a page yet.", { icon: "🎬" });
            }}
            className="absolute left-[41%] -translate-x-1/2 sm:left-1/2 flex items-center gap-1.5 text-white font-extrabold hover:opacity-80 transition-opacity whitespace-nowrap"
            data-testid="community-manual-avatar"
            title="View creator"
          >
            <span className="text-[11px] sm:text-xs font-bold text-white/75 uppercase tracking-wide">User ID:</span>
            <span className="text-xl leading-none">{manual.avatar.emoji}</span>
            <span className="tabular-nums tracking-wide">{manual.avatar.tag}</span>
          </button>
        )}
        {(nextInfo?.next_id || nextInfo?.prev_id) ? (
          <div className="flex items-center gap-4">
            {nextInfo?.prev_id ? (
              <button
                onClick={goPrev}
                data-testid="prev-video-btn"
                title={manual?.format === "podcast" ? "Previous podcast — or swipe right on mobile" : "Previous video — or swipe right on mobile"}
                className="flex items-center gap-2 text-white font-bold hover:text-white/80 transition-colors"
              >
                <ArrowLeft size={18} /> Back
              </button>
            ) : <span className="w-4" />}
            {nextInfo?.next_id ? (
              <button
                onClick={goNext}
                data-testid="next-video-btn"
                title={manual?.format === "podcast" ? "Next podcast — or swipe left on mobile" : "Next video — or swipe left on mobile"}
                className="flex items-center gap-2 text-white font-bold hover:text-white/80 transition-colors"
              >
                Next <ArrowRight size={18} />
              </button>
            ) : <span className="w-4" />}
          </div>
        ) : <span className="w-4" />}
      </div>

      <div className="sms-card sms-dark-page overflow-hidden sms-fade-up">
        <div className="w-full aspect-video bg-[#100d1c] relative overflow-hidden">
          {(manual.video_file_id || manual.video_storage_path) && playing ? (
            <video
              data-testid="community-inline-video"
              ref={inlineVideoRef}
              src={`${API}/community/${id}/video/stream`}
              controls
              autoPlay
              playsInline
              onError={onVideoError}
              className="w-full h-full object-contain bg-black"
            />
          ) : (
            <>
              {manual.hero_image_id && <img src={imageUrl(manual.hero_image_id)} alt={manual.title} className="w-full h-full object-cover" />}
              {(manual.video_file_id || manual.video_storage_path) && (
                <button
                  data-testid="community-play-poster"
                  onClick={() => setPlaying(true)}
                  className="absolute inset-0 flex items-center justify-center group"
                  aria-label="Play video"
                >
                  <span className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
                  <span className="relative w-14 h-14 rounded-full bg-[#4f7746] group-hover:bg-[#3f6238] flex items-center justify-center shadow-lg transition-transform group-hover:scale-105">
                    <Play size={22} className="text-white ml-0.5" fill="#fff" />
                  </span>
                </button>
              )}
            </>
          )}
          {(manual.video_file_id || manual.video_storage_path) && (
            <button
              data-testid="community-expand-video"
              onClick={openExpanded}
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/55 hover:bg-black/75 text-white flex items-center justify-center transition-colors"
              aria-label="Expand video"
              title="Expand video"
            >
              <Maximize2 size={16} />
            </button>
          )}
        </div>
        <div className="px-6 sm:px-8 pt-3 pb-6 sm:pb-8">
            {/* Row 1: In-Depth (left) · Category (center) · Swipe (right) */}
            <div className="flex items-center gap-2 text-sm" data-testid="community-controls-row">
              <div className="flex-1 flex justify-start min-w-0">
                <AudienceBadge audience={manual.audience} topic={manual.title} dark />
              </div>
              <div className="flex-1 flex justify-center min-w-0">
                <span className="font-bold text-black bg-[#ffd21e] px-2.5 py-1 rounded-full text-xs uppercase tracking-wide whitespace-nowrap">{manual.share_category}</span>
              </div>
              <div className="flex-1 flex justify-end min-w-0">
                {nextInfo?.next_id && (
                  <button
                    data-testid="swipe-hint"
                    onClick={goNext}
                    title="Swipe left for the next video, right for the previous — or tap here"
                    className="select-none flex items-center gap-1 text-[#5d8a52] hover:text-[#3f6238] transition-colors sms-swipe-hint-x"
                  >
                    <ArrowLeft size={16} strokeWidth={2.6} />
                    <span className="text-xs font-extrabold tracking-wide leading-none">Swipe</span>
                    <ArrowRight size={16} strokeWidth={2.6} />
                  </button>
                )}
              </div>
            </div>

            {/* Row 2: Views (left) · Star rating (center) · Love + Tip (right) */}
            <div className="flex items-center gap-2 text-sm mt-3">
              <div className="flex-1 flex justify-start min-w-0">
                <span className="flex items-center gap-1 text-[#9a96ac]" data-testid="community-manual-views"><Eye size={14} /> {manual.views ?? 0} views</span>
              </div>
              <div className="flex-1 flex justify-center min-w-0">
                <StarRating value={avg.rating_avg} count={avg.rating_count} size={16} />
              </div>
              <div className="flex-1 flex justify-end items-center gap-3 min-w-0">
                {manual.is_owner ? (
                  <span data-testid="loves-received-badge" title={`Your video has received ${manual.loves || 0} love`} className="flex items-center gap-1 text-[#e11d48] font-extrabold text-sm"><Heart size={15} fill="#e11d48" /> {manual.loves || 0}</span>
                ) : (
                  <button data-testid="community-love-btn" onClick={toggleLove} disabled={loveBusy}
                    title={manual.i_loved ? "You love this — tap to undo" : "Show some love"} aria-label="Show some love"
                    className="flex items-center gap-1 text-sm font-extrabold transition-transform active:scale-95 disabled:opacity-60" style={{ color: "#e11d48" }}>
                    <Heart size={16} fill={manual.i_loved ? "#e11d48" : "none"} /> {manual.loves || 0}
                  </button>
                )}
                {!manual.is_owner && (
                  <button
                    data-testid="tip-btn"
                    onClick={openTip}
                    title="Send the creator a tip"
                    aria-label="Send a tip"
                    className="inline-flex items-center gap-1.5 text-xs font-extrabold px-3 py-1.5 rounded-full text-white transition-transform active:scale-95"
                    style={{ background: "linear-gradient(135deg,#e0a92e,#c2410c)", boxShadow: "0 2px 8px rgba(194,65,12,.25)" }}
                  >
                    <DollarSign size={14} strokeWidth={2.5} /> Tip
                  </button>
                )}
              </div>
            </div>

            {/* Utility actions — Path · Channel · Save · Share · Link · source(YouTube) */}
            <div className="mt-3 grid grid-cols-6 gap-1 max-w-lg mx-auto" data-testid="community-actions-row">
              <button
                data-testid="community-path-btn"
                onClick={() => manual.share_category && navigate(`/path/${encodeURIComponent(manual.share_category)}`)}
                title={`Explore the ${manual.share_category || ""} Path community`}
                aria-label="Explore this Path community"
                className="flex w-full items-center justify-center gap-1 text-[10px] font-bold text-white px-0.5 py-1.5 rounded-full transition-colors whitespace-nowrap"
                style={{ background: "#2e7d32" }}
              >
                <Compass size={11} /> Path
              </button>
              <button
                data-testid="community-channel-btn"
                onClick={() => {
                  if (manual.creator_has_page && manual.creator_username) navigate(`/creator/${manual.creator_username}`);
                  else if (user && manual.creator_username && user.username === manual.creator_username) setUnlockOpen(true);
                  else toast("This creator hasn't unlocked a page yet.", { icon: "🎬" });
                }}
                title="Visit this creator's channel"
                aria-label="Visit this creator's channel"
                className="flex w-full items-center justify-center gap-1 text-[10px] font-bold text-white px-0.5 py-1.5 rounded-full transition-colors whitespace-nowrap"
                style={{ background: "#1e3aaa" }}
              >
                <Users size={11} /> Channel
              </button>
              <button
                data-testid="community-save-btn"
                onClick={toggleSaveVideo}
                disabled={saveBusy}
                title={videoSaved ? "Saved — tap to remove" : "Save this video"}
                aria-label={videoSaved ? "Remove from saved" : "Save this video"}
                className="flex w-full items-center justify-center gap-1 text-[10px] font-bold text-white px-0.5 py-1.5 rounded-full transition-colors disabled:opacity-60 whitespace-nowrap"
                style={{ background: videoSaved ? "#16308c" : "#1e3aaa" }}
              >
                {videoSaved ? <><BookmarkCheck size={11} /> Saved</> : <><Bookmark size={11} /> Save</>}
              </button>
              <button
                data-testid="community-share-btn"
                onClick={shareManual}
                title="Share this video"
                aria-label="Share this video"
                className="flex w-full items-center justify-center gap-1 text-[10px] font-bold text-white px-0.5 py-1.5 rounded-full transition-colors whitespace-nowrap"
                style={{ background: "#1e3aaa" }}
              >
                <Share size={11} /> Share
              </button>
              <button
                data-testid="community-videolink-btn"
                onClick={copyVideoLink}
                title="Copy this video's link to your clipboard"
                aria-label="Copy this video's link"
                className="flex w-full items-center justify-center gap-1 text-[10px] font-bold text-white px-0.5 py-1.5 rounded-full transition-colors whitespace-nowrap"
                style={{ background: "#1e3aaa" }}
              >
                <Copy size={11} /> Link
              </button>
              {manual.source_url ? (() => {
                const meta = sourceMeta(manual.source_url);
                const Icon = meta.Icon;
                return (
                  <a
                    data-testid="source-link-btn"
                    href={manual.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex w-full items-center justify-center gap-1 font-bold rounded-full px-0.5 py-1.5 text-white text-[10px] transition-colors hover:opacity-95 whitespace-nowrap"
                    style={{ background: "#1e3aaa" }}
                  >
                    <Icon size={11} /> {meta.label}
                  </a>
                );
              })() : (
                <div data-testid="source-link-btn-ghost" title="No source video linked to this one" aria-disabled="true"
                  className="flex w-full items-center justify-center gap-1 font-bold rounded-full px-0.5 py-1.5 text-[10px] text-white/80 cursor-not-allowed select-none whitespace-nowrap"
                  style={{ background: "#9aa9d6" }}>
                  <Youtube size={11} /> YouTube
                </div>
              )}
            </div>

            {/* Full-kit bundle — buy once, own everything (not for podcasts) */}
            {manual.format !== "podcast" && mats && (() => {
              const b = mats.bundle || {};
              const included = mats.included || [];
              const owned = b.owned || mats.is_owner;
              if (guestKitSid && !owned) {
                return (
                  <div className="mt-3 max-w-md mx-auto rounded-2xl border-2 border-[#1e3aaa] bg-[#eef2ff] p-4 text-center" data-testid="kit-guest-download">
                    <div className="inline-flex items-center gap-1.5 text-sm font-extrabold text-[#1e3aaa]"><Check size={16} /> Purchase complete — your kit is ready!</div>
                    <button
                      data-testid="guest-download-kit-btn"
                      onClick={() => downloadGuestKit(guestKitSid)}
                      className="mt-3 w-full inline-flex items-center justify-center gap-2 font-extrabold text-white py-2.5 rounded-full transition-transform hover:scale-[1.01]"
                      style={{ background: "#1e3aaa", border: "2px solid #16308c" }}
                    >
                      <FileDown size={16} /> Download your kit (.zip)
                    </button>
                    <p className="text-[11px] text-[#6b6880] mt-2">Bookmark this page — you can re-download from this link. Create a free account anytime to keep it in your Purchases.</p>
                  </div>
                );
              }
              if (owned) {
                return (
                  <div className="mt-3 max-w-md mx-auto rounded-2xl border-2 border-[#1e3aaa] bg-[#0f0f16] sms-tweed text-white p-3.5" data-testid="kit-owned">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-sm font-extrabold text-[#8fbf82]">
                        <Check size={15} className="text-[#5d8a52]" /> Assets
                      </div>
                      <button
                        data-testid="kit-download-all-btn"
                        onClick={downloadAllKit}
                        disabled={kitZipBusy}
                        className="inline-flex items-center gap-1.5 text-[12px] font-extrabold text-white px-3 py-1 rounded-full transition-transform hover:scale-105 disabled:opacity-70"
                        style={{ background: "#1e3aaa", border: "1px solid #16308c" }}
                      >
                        {kitZipBusy ? <Loader2 size={13} className="spin" /> : <FileDown size={13} />} Download All — {(Number(b.list_price_usd) || 0) > 0 ? `$${Number(b.list_price_usd).toFixed(2)}` : "Free"}
                      </button>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="kit-download-chips">
                      {included.map((it) => renderKitChip(it))}
                    </div>
                  </div>
                );
              }
              // Non-owner view — one adaptive "Download All" pill: guests → Join Us; members → the price
              // (free members download; priced members buy). Price is always shown once signed in.
              const price = Number(b.list_price_usd || 0);
              const isFree = !!b.free;
              const canGrab = isFree && !!user;          // signed-in community member, free kit → grab now
              const mustJoin = !user;                    // guest → must join to access anything
              const pillBusy = canGrab ? kitZipBusy : (!mustJoin ? buyBusy : false);
              const pillAction = mustJoin ? () => setJoinOpen(true) : (canGrab ? downloadAllKit : buyKit);
              return (
                <div className="mt-3 max-w-md mx-auto rounded-2xl border-2 border-[#1e3aaa] bg-[#f6faf3] p-3.5" data-testid="kit-public">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 text-sm font-extrabold text-[#3f6238]">
                      <Package size={15} className="text-[#5d8a52]" /> Assets
                    </div>
                    <button
                      data-testid="kit-download-all-btn"
                      onClick={pillAction}
                      disabled={pillBusy}
                      className="inline-flex items-center gap-1.5 text-[12px] font-extrabold text-white px-3 py-1 rounded-full transition-transform hover:scale-105 disabled:opacity-70"
                      style={{ background: "#1e3aaa", border: "1px solid #16308c" }}
                    >
                      {pillBusy ? <Loader2 size={13} className="spin" /> : (mustJoin ? <Users size={13} /> : <FileDown size={13} />)} Download All — {mustJoin ? "Join Us" : (price > 0 ? `$${price.toFixed(2)}` : "Free")}
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="kit-includes-list">
                    {included.map((it) => (
                      <span key={it.asset} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#6b6880] bg-[#eef3ea] rounded-full px-2.5 py-1">
                        <Check size={11} className="text-[#5d8a52]" /> {it.label}
                      </span>
                    ))}
                  </div>
                  <p className="text-[11px] text-[#9a96ac] mt-2">
                    {mustJoin
                      ? "Join our Vittyyo community to download this kit."
                      : (canGrab
                        ? "Free for community members — tap Download All to grab everything."
                        : "Buy once, own it forever — re-download anytime from your Purchases.")}
                  </p>
                </div>
              );
            })()}

            {/* Classic Car LLM — guardrailed AI chat, only on the Plymouth Hemi 'Cuda video */}
            {/* AI Expert — guardrailed, auto-scoped chatbot. Shown when the creator has enabled it. */}
            {manual.expert?.enabled && (
              <ExpertChat
                manualId={manual.id}
                expert={manual.expert}
                isOwner={manual.is_owner}
                onTopup={() =>
                  api.get(`/community/${id}`).then(({ data }) => setManual(data)).catch(() => {})
                }
              />
            )}

            <h1 className="text-2xl md:text-3xl font-extrabold mt-3 leading-tight" data-testid="community-manual-title">{manual.title}</h1>
            {manual.description && manual.description.trim() && (
              <p className="text-[15px] text-[#4a475c] mt-2 leading-relaxed whitespace-pre-line" data-testid="community-manual-description">
                {manual.description.trim()}
              </p>
            )}

            {/* Rate — tap a star (kept right above comments for easy reach) */}
            <div className="mt-5 rounded-2xl sms-tweed bg-[#0f0f16] border-2 border-[#5d8a52] p-4 text-center" data-testid="community-rate">
              <div className="font-bold mb-2 text-white">Was this helpful? Tap to rate it</div>
              <div className="flex justify-center">
                <StarRating value={myRating} interactive size={30} onRate={rate} />
              </div>
              {myRating > 0 && <p className="text-sm text-white/60 mt-2">You rated this {myRating} star{myRating > 1 ? "s" : ""}.</p>}
            </div>

            {manual.share_category && (
              <button
                data-testid="join-path-cta"
                onClick={() => navigate(`/path/${encodeURIComponent(manual.share_category)}`)}
                className="mt-5 w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl text-left group hover:brightness-110 transition-all"
                style={{ background: "#0f0f16", border: "2px solid #5d8a52", boxShadow: "0 0 22px rgba(93,138,82,0.28)" }}
              >
                <span className="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" style={{ background: "#16241a", border: "1px solid #5d8a52" }}>
                  <Users size={20} className="text-[#8fbf82]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-extrabold text-white leading-tight">Join the <span className="text-[#8fbf82]">{manual.share_category}</span> Path</span>
                  <span className="block text-xs text-white/55">A living hub of {manual.share_category} videos, makers &amp; real results — meet your people.</span>
                </span>
                <span className="text-[#8fbf82] shrink-0 group-hover:translate-x-1 transition-transform"><ArrowRight size={20} strokeWidth={2.6} /></span>
              </button>
            )}

            <div className="mt-5 rounded-2xl overflow-hidden" style={{ background: "#0f0f16", border: "2px solid #5d8a52" }} data-testid="did-it-card">
              <button
                data-testid="did-it-toggle"
                onClick={() => setDiOpen((o) => !o)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/5 transition-colors"
              >
                <span className="flex items-center gap-2.5 min-w-0">
                  <span className="w-9 h-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "#16241a", border: "1px solid #5d8a52" }}>
                    <Check size={18} className="text-[#8fbf82]" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-extrabold text-white leading-tight">{didItVerb}</span>
                    <span className="block text-xs text-white/55 truncate">
                      {didIt.count > 0 ? `${didIt.count} ${didIt.count === 1 ? "person" : "people"} did it — show your proof` : "Be the first to prove you did it"}
                    </span>
                  </span>
                </span>
                <span className="text-xs font-bold text-[#8fbf82] shrink-0">{diOpen ? "Close" : "＋ Add yours"}</span>
              </button>

              {diOpen && (
                <div className="px-4 pb-4 pt-1 space-y-3 border-t border-white/10" data-testid="did-it-form">
                  <p className="text-[11px] text-white/50">This is a supercharged comment — proof required. Upload a photo of your result, or link your own Vittyyo version.</p>
                  <textarea
                    data-testid="did-it-text" value={diText} maxLength={200} onChange={(e) => setDiText(e.target.value)} rows={2}
                    placeholder='Tell us how it went… e.g. "Made this lasagna and added peppers — so good!"'
                    className="w-full px-3 py-2 rounded-xl text-sm text-white outline-none resize-none" style={{ background: "#14141b", border: "1.5px solid #2a3a26" }}
                  />
                  <div className="flex items-center justify-between gap-2">
                    <label data-testid="did-it-photo-btn" className="inline-flex items-center gap-1.5 text-xs font-bold text-white px-3 py-2 rounded-full cursor-pointer" style={{ background: "#16241a", border: "1px solid #5d8a52" }}>
                      <Images size={14} className="text-[#8fbf82]" /> {diPhoto ? "Photo added ✓" : "Upload photo"}
                      <input type="file" accept="image/*" className="hidden" onChange={onDiPhoto} />
                    </label>
                    {diPhoto && <button onClick={() => setDiPhoto("")} className="text-[11px] text-white/40 underline">remove</button>}
                    <button
                      data-testid="did-it-paste-btn"
                      onClick={pasteDidItLink}
                      className="inline-flex items-center gap-1.5 text-xs font-bold text-white px-3 py-2 rounded-full ml-auto"
                      style={{ background: "#1e3aaa" }}
                    >
                      <Copy size={14} /> Paste link
                    </button>
                  </div>
                  <input
                    data-testid="did-it-link" value={diLink} maxLength={300} onChange={(e) => setDiLink(e.target.value)}
                    placeholder="…or paste a link to your Vittyyo video/channel"
                    className="w-full px-3 py-2 rounded-xl text-sm text-white outline-none" style={{ background: "#14141b", border: "1.5px solid #2a3a26" }}
                  />
                  <button
                    data-testid="did-it-submit" onClick={submitDidIt} disabled={diBusy}
                    className="w-full font-extrabold text-white py-2.5 rounded-full disabled:opacity-50" style={{ background: "#4f7746" }}
                  >
                    {diBusy ? "Posting…" : `✓ ${didItVerb}`}
                  </button>
                </div>
              )}

              {didIt.entries.length > 0 && (
                <div className="px-4 pb-4 space-y-2.5 border-t border-white/10 pt-3" data-testid="did-it-wall">
                  {didIt.entries.map((e) => (
                    <div key={e.id} className="flex gap-3" data-testid={`did-it-entry-${e.id}`}>
                      {e.photo_id && (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/images/${e.photo_id}`} alt="result" className="w-16 h-16 rounded-lg object-cover shrink-0" style={{ border: "1px solid #2a3a26" }} />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-xs">
                          <span className="font-bold text-[#8fbf82]">{e.author_emoji}{e.author_tag}</span>
                          <span className="text-[#8fbf82]/70 flex items-center gap-0.5"><Check size={12} /> did it</span>
                          {e.mine && <button onClick={() => deleteDidIt(e.id)} className="ml-auto text-[10px] text-white/30 underline">remove</button>}
                        </div>
                        {e.text && <p className="text-sm text-white/85 mt-0.5">{e.text}</p>}
                        {e.link && <a href={e.link} target="_blank" rel="noopener noreferrer" className="text-xs text-[#5b8cff] underline break-all">View their version →</a>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-5" data-testid="community-comments">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-[#4a475c] font-bold tracking-wide text-sm uppercase">
                  <MessageCircle size={16} className="text-[#5d8a52]" /> Comments
                  {comments.length > 0 && <span className="text-[#9a96ac] normal-case font-semibold">({comments.length})</span>}
                </div>
                <div className="flex items-center gap-2.5">
                  {comments.length > 0 && (
                    <button
                      data-testid="view-all-comments-btn"
                      onClick={() => setCommentsOpen(true)}
                      className="text-xs font-bold text-[#5d8a52] hover:text-[#3f6238] transition-colors"
                    >
                      View all
                    </button>
                  )}
                  <button
                    data-testid="comments-flag-btn"
                    onClick={() => setReportOpen(true)}
                    title="Report a problem in the comments"
                    className="text-[#c0402b] hover:text-[#9a2f20] transition-colors p-1 -mr-1"
                  >
                    <Flag size={17} fill="#c0402b" />
                  </button>
                </div>
              </div>
              <div className="rounded-2xl border border-[#5d8a52] bg-[#f7faf5] p-3">
                {comments.length === 0 ? (
                  <p className="text-sm text-[#9a96ac] px-1 py-1.5">Be the first to say something nice 👋</p>
                ) : (
                  <div className="max-h-44 overflow-y-auto space-y-2 pr-1" data-testid="comments-list">
                    {comments.map((c) => (
                      <div key={c.id} className="flex items-start gap-2 text-sm group" data-testid={`comment-${c.id}`}>
                        <span className="shrink-0 flex items-center gap-0.5 text-xs font-extrabold text-[#171426] bg-[#eef3ea] rounded-full px-1.5 py-0.5 leading-5" data-testid={`comment-avatar-${c.id}`}>
                          <span>{c.emoji || "💬"}</span>{c.author && <span className="tabular-nums tracking-wide">{c.author}</span>}
                        </span>
                        <div className="min-w-0 flex-1">
                          <span className="text-[#3a3750] break-words">{c.text}</span>
                          {c.created_at && <span className="text-[11px] text-[#a7a3b8] ml-1.5 whitespace-nowrap" data-testid={`comment-time-${c.id}`}>· {timeAgo(c.created_at)}</span>}
                          {c.reply && (
                            <div className="mt-1 flex items-start gap-1.5 rounded-lg bg-[#eef3ea] px-2 py-1" data-testid={`comment-creator-reply-${c.id}`}>
                              <span className="text-[#4f7746] text-xs mt-0.5">↳</span>
                              <div className="text-xs text-[#3a3750] break-words"><span className="font-extrabold text-[#4f7746]">Creator</span> · {c.reply.text}</div>
                            </div>
                          )}
                        </div>
                        {(user?.unlimited || c.mine) && (
                          <button
                            data-testid={`delete-comment-${c.id}`}
                            onClick={() => removeComment(c.id)}
                            title="Delete"
                            className="opacity-0 group-hover:opacity-100 text-[#c0402b] shrink-0 transition-opacity"
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-2.5 pt-2.5 border-t border-[#e6ede1]">
                  <input
                    data-testid="comment-input"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value.slice(0, 100))}
                    onKeyDown={(e) => { if (e.key === "Enter") postComment(); }}
                    maxLength={100}
                    placeholder={user ? "Add a comment…" : "Log in to comment"}
                    disabled={!user || posting}
                    className="flex-1 min-w-0 text-sm bg-white border border-[#5d8a52] rounded-full px-4 py-2 outline-none focus:border-[#bcd2b0] transition-colors disabled:opacity-60"
                  />
                  <span className="text-[10px] text-[#b7b3c8] tabular-nums w-9 text-right shrink-0">{commentText.length}/100</span>
                  <button
                    data-testid="comment-post-btn"
                    onClick={postComment}
                    disabled={!user || posting || !commentText.trim()}
                    className="shrink-0 inline-flex items-center gap-1.5 font-bold text-white text-sm bg-[#4f7746] hover:bg-[#3f6238] rounded-full px-4 py-2 transition-colors disabled:opacity-50"
                  >
                    {posting ? <Loader2 size={15} className="spin" /> : <Send size={15} />} Post
                  </button>
                </div>
              </div>
            </div>
          </div>

        <div className="px-6 pb-6">
          {(manual.supplies?.length > 0 || items?.length > 0 || manual.format === "podcast") && (
            <div className="mt-6" data-testid="community-supplies">
              <div className="flex items-center gap-2 text-[#4a475c] font-bold tracking-wide text-sm uppercase"><ShoppingBag size={16} className="text-[#5d8a52]" /> {manual.format === "podcast" ? "Recommended for this episode" : "Shop the supplies"}</div>

              {items === null ? (
                <div className="py-10 flex flex-col items-center gap-3 text-[#6b6880]">
                  <Loader2 size={24} className="spin text-[#5d8a52]" />
                  <p className="text-sm font-medium">{manual.format === "podcast" ? "Curating picks for this episode…" : "Finding the best-rated products…"}</p>
                </div>
              ) : items.length === 0 ? (
                <ul className="mt-3 space-y-1">
                  {(manual.supplies || []).map((s, i) => (
                    <li key={i} className="flex items-center gap-2.5 text-[#2c2941] py-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#a9c39c] shrink-0" />
                      <a href={buildItemUrl("Amazon", s, amazonTag)} target="_blank" rel="noopener noreferrer" className="flex-1 hover:text-[#4f7746] hover:underline transition-colors">{s}</a>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="mt-3 space-y-2">
                  {items.map((it, i) => (
                    <div key={i} data-testid={`community-shop-item-${i}`} className="sms-tweed rounded-2xl border-2 border-[#5d8a52] bg-[#0f0f16] text-white p-3 sms-fade-up" style={{ animationDelay: `${i * 0.04}s` }}>
                      <div className="min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          {it.supply ? <span className="text-xs font-bold tracking-wide uppercase text-[#9a96ac]">{it.supply}</span> : <span />}
                          {it.price_range && (
                            <span className="text-xs font-bold text-white bg-[#1e3aaa] px-2.5 py-1 rounded-full whitespace-nowrap shrink-0">{it.price_range}</span>
                          )}
                        </div>
                        <div className="font-extrabold text-base leading-snug mt-0.5">{it.product_name}</div>
                        {it.description && <p className="text-[#6b6880] text-sm mt-1 leading-relaxed">{it.description}</p>}
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <button
                            data-testid={`buy-later-${i}`}
                            onClick={() => saveItem(it, i)}
                            disabled={saved[i]}
                            className={`inline-flex items-center gap-1.5 text-sm font-bold px-3 py-2 rounded-full transition-colors text-white ${saved[i] ? "bg-[#22a06b] cursor-default" : "bg-[#4f7746] hover:bg-[#3f6238]"}`}
                          >
                            {saved[i] ? <><BookmarkCheck size={15} /> Saved</> : <><Bookmark size={15} /> Save for later</>}
                          </button>
                          <a
                            data-testid={`buy-link-${i}`}
                            href={it.url || buildItemUrl("Amazon", it.search_query || it.product_name, amazonTag)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 font-bold text-black bg-[#ffd814] hover:bg-[#f7ca00] px-4 py-2 rounded-full text-sm transition-colors"
                          >
                            <ExternalLink size={15} /> Buy on Amazon
                          </a>
                          {user?.unlimited && (
                            <button
                              data-testid={`delete-link-${i}`}
                              onClick={() => deleteItem(i)}
                              title="Remove this Amazon link"
                              className="inline-flex items-center justify-center w-9 h-9 rounded-full text-[#dc2626] bg-[#fdecec] hover:bg-[#fbdcdc] transition-colors ml-auto"
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-[#b7b3c8] mt-3" data-testid="amazon-disclosure-community">As an Amazon Associate, Vittyyo earns from qualifying purchases. Prices are approximate.</p>
            </div>
          )}

          {manual.safety_warnings?.length > 0 && (
            <div className="mt-6 rounded-2xl p-4" style={{ background: "#0f0f16", border: "2px solid #5d8a52" }} data-testid="safety-panel">
              <div className="flex items-center gap-2 text-[#e0a92e] font-bold tracking-wide text-sm uppercase"><span aria-hidden="true">⚠️</span> Safety <span aria-hidden="true">⚠️</span></div>
              <ul className="mt-2 space-y-1.5">
                {manual.safety_warnings.map((w, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-white/85"><span className="w-1.5 h-1.5 rounded-full bg-[#e0a92e] mt-2.5 shrink-0" />{w}</li>
                ))}
              </ul>
            </div>
          )}

          {manual.steps?.length > 0 && (
            <div className="mt-7">
              <div className="text-[#4a475c] font-bold tracking-wide text-sm uppercase mb-3">Steps</div>
              <div className="space-y-3">
                {manual.steps.map((st, i) => (
                  <div key={i} className="flex gap-3" data-testid={`community-step-${i}`}>
                    <div className="w-8 h-8 rounded-xl flex items-center justify-center text-white font-extrabold text-sm shrink-0"
                      style={{ background: "#1e3aaa" }}>{i + 1}</div>
                    <div>
                      <div className="font-bold leading-snug">{st.title}</div>
                      {st.why && <p className="text-sm text-[#6b6880] mt-0.5">{st.why}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <PrintableManual manual={manual} />

      {expanded && (manual.video_file_id || manual.video_storage_path) && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center p-4"
          style={{ background: "rgba(10,8,20,.85)", backdropFilter: "blur(6px)" }}
          onClick={() => setExpanded(false)}
          data-testid="community-video-modal"
        >
          <button
            data-testid="community-video-modal-close"
            onClick={() => setExpanded(false)}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10"
            aria-label="Close"
          >
            <X size={22} />
          </button>
          <div
            className="relative w-full"
            style={{ maxWidth: (manual.video_orientation === "portrait") ? "min(440px, 96vw)" : "min(1100px, 96vw)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <video
              data-testid="community-modal-video"
              src={`${API}/community/${id}/video/stream`}
              controls
              autoPlay
              playsInline
              onError={onVideoError}
              className="w-full rounded-2xl bg-black shadow-2xl"
              style={{ maxHeight: "90vh" }}
            />
          </div>
        </div>
      )}
      {reportOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50"
          data-testid="report-modal"
          onClick={() => !reportBusy && setReportOpen(false)}
        >
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 shadow-2xl sms-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-1">
              <Flag size={18} fill="#c0402b" className="text-[#c0402b]" />
              <h3 className="text-lg font-extrabold text-[#171426]">Report a problem</h3>
            </div>
            <p className="text-sm text-[#6b6880] mb-3">What's wrong with the comments here? Pick a reason — our team will review.</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {["Profanity", "Adult content", "Inappropriate theme", "Crime / illegal", "Bullying / harassment", "Other"].map((cat) => (
                <button
                  key={cat}
                  data-testid={`report-cat-${cat}`}
                  onClick={() => setReportCategory(cat)}
                  className={`text-xs font-bold px-3 py-1.5 rounded-full border-2 transition-colors ${reportCategory === cat ? "bg-[#4f7746] text-white border-[#4f7746]" : "bg-white text-[#4a475c] border-[#5d8a52] hover:bg-[#f1f4ee]"}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <textarea
              data-testid="report-note"
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value.slice(0, 300))}
              rows={3}
              maxLength={300}
              placeholder="Add a detail (optional) — which comment, and why…"
              className="w-full text-sm bg-white border border-[#5d8a52] rounded-xl px-3 py-2 outline-none focus:border-[#bcd2b0] resize-none"
            />
            <div className="flex items-center justify-end gap-2 mt-3">
              <button
                data-testid="report-cancel"
                onClick={() => setReportOpen(false)}
                disabled={reportBusy}
                className="text-sm font-bold text-[#6b6880] px-4 py-2 rounded-full hover:bg-[#f1f4ee] transition-colors"
              >
                Cancel
              </button>
              <button
                data-testid="report-submit"
                onClick={submitReport}
                disabled={reportBusy || !reportCategory}
                className="text-sm font-bold text-white bg-[#c0402b] hover:bg-[#9a2f20] rounded-full px-4 py-2 disabled:opacity-50 inline-flex items-center gap-1.5 transition-colors"
              >
                {reportBusy ? <Loader2 size={15} className="spin" /> : <Flag size={14} />} Submit report
              </button>
            </div>
          </div>
        </div>
      )}
      {commentsOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50"
          data-testid="comments-modal"
          onClick={() => setCommentsOpen(false)}
        >
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col max-h-[85vh] sm:max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#eef0ec]">
              <div className="flex items-center gap-2 font-extrabold text-[#171426]">
                <MessageCircle size={18} className="text-[#5d8a52]" /> Comments <span className="text-[#9a96ac] font-semibold">({comments.length})</span>
              </div>
              <button data-testid="comments-modal-close" onClick={() => setCommentsOpen(false)} className="text-[#9a96ac] hover:text-[#171426] transition-colors"><X size={20} /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="comments-modal-list">
              {comments.length === 0 ? (
                <p className="text-sm text-[#9a96ac] py-6 text-center">No comments yet — be the first 👋</p>
              ) : comments.map((c) => (
                <div key={c.id} className="flex items-start gap-2.5 text-sm group">
                  <span className="shrink-0 text-lg leading-6">{c.emoji || "💬"}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="font-extrabold text-[#171426]">{c.author}</span>
                      {c.created_at && <span className="text-[11px] text-[#a7a3b8]">{timeAgo(c.created_at)}</span>}
                    </div>
                    <p className="text-[#3a3750] break-words leading-snug">{c.text}</p>
                  </div>
                  {(user?.unlimited || c.mine) && (
                    <button data-testid={`modal-delete-comment-${c.id}`} onClick={() => removeComment(c.id)} className="opacity-0 group-hover:opacity-100 text-[#c0402b] shrink-0 transition-opacity" title="Delete"><Trash2 size={14} /></button>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 px-4 py-3 border-t border-[#eef0ec]">
              <input
                data-testid="comment-input-modal"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value.slice(0, 100))}
                onKeyDown={(e) => { if (e.key === "Enter") postComment(); }}
                maxLength={100}
                placeholder={user ? "Add a comment…" : "Log in to comment"}
                disabled={!user || posting}
                className="flex-1 min-w-0 text-sm bg-white border border-[#5d8a52] rounded-full px-4 py-2 outline-none focus:border-[#bcd2b0] transition-colors disabled:opacity-60"
              />
              <span className="text-[10px] text-[#b7b3c8] tabular-nums w-9 text-right shrink-0">{commentText.length}/100</span>
              <button
                data-testid="comment-post-modal-btn"
                onClick={postComment}
                disabled={!user || posting || !commentText.trim()}
                className="shrink-0 inline-flex items-center gap-1.5 font-bold text-white text-sm bg-[#4f7746] hover:bg-[#3f6238] rounded-full px-4 py-2 transition-colors disabled:opacity-50"
              >
                {posting ? <Loader2 size={15} className="spin" /> : <Send size={15} />} Post
              </button>
            </div>
          </div>
        </div>
      )}
      {tipOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm"
          data-testid="tip-modal"
          onClick={() => !tipBusy && setTipOpen(false)}
        >
          <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl sms-fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-extrabold flex items-center gap-2 text-[#171426]">
                <span className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg,#e0a92e,#c2410c)" }}><Heart size={17} className="text-white" /></span>
                Send a Tip
              </h2>
              <button data-testid="tip-close" onClick={() => setTipOpen(false)} className="text-[#b7b3c8] hover:text-[#6b6880]"><X size={18} /></button>
            </div>
            <p className="text-sm text-[#6b6880] mb-4">
              Say thanks to {manual.avatar ? <span className="font-extrabold text-[#171426]">{manual.avatar.emoji}{manual.avatar.tag}</span> : "this creator"} for making great videos. You'll pay securely by card — it goes straight to them.
            </p>

            <div className="flex items-center justify-center gap-2 mb-4" data-testid="tip-amounts">
              {[1, 2, 3, 4, 5].map((n) => {
                const maxDollars = tipAllowance ? Math.min(5, Math.floor((tipAllowance.daily_remaining_cents ?? 500) / 100)) : 5;
                const disabled = n > maxDollars;
                const active = tipAmount === n;
                return (
                  <button
                    key={n}
                    data-testid={`tip-amount-${n}`}
                    disabled={disabled}
                    onClick={() => setTipAmount(n)}
                    className="w-11 h-11 rounded-2xl font-extrabold text-sm flex items-center justify-center transition-transform active:scale-90 disabled:opacity-30 disabled:cursor-not-allowed"
                    style={active
                      ? { background: "linear-gradient(135deg,#e0a92e,#c2410c)", color: "#fff", boxShadow: "0 4px 14px rgba(194,65,12,.3)" }
                      : { background: "#fdf3e0", color: "#b45309", boxShadow: "inset 0 0 0 1.5px #f0d190" }}
                  >
                    ${n}
                  </button>
                );
              })}
            </div>

            <input
              data-testid="tip-message"
              value={tipMsg}
              onChange={(e) => setTipMsg(e.target.value.slice(0, 140))}
              placeholder="Add a message (optional) — “Great videos!”"
              className="w-full text-sm bg-white border-2 border-[#f0d190] rounded-2xl px-4 py-2.5 outline-none focus:border-[#e0a92e] transition-colors mb-3"
            />

            <button
              data-testid="tip-send"
              onClick={sendTip}
              disabled={tipBusy || tipAmount < 1}
              className="w-full text-white font-extrabold py-3 rounded-2xl flex items-center justify-center gap-2 disabled:opacity-50 transition-transform active:scale-[0.99]"
              style={{ background: "linear-gradient(135deg,#e0a92e,#c2410c)" }}
            >
              {tipBusy ? <Loader2 size={17} className="spin" /> : <Heart size={17} />}
              {tipAmount > 0 ? `Continue to pay $${tipAmount}.00` : "No tips left today"}
            </button>
            <p className="text-[11px] text-[#9a96ac] text-center mt-2.5">
              $1–$5 per tip · paid by card · goes straight to the creator's Stripe account
            </p>
          </div>
        </div>
      )}

      {joinOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm"
          data-testid="join-prompt-modal"
          onClick={() => setJoinOpen(false)}
        >
          <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl sms-fade-up text-center" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-full bg-[#eef3ea] flex items-center justify-center mx-auto mb-3">
              <Users size={24} className="text-[#4f7746]" />
            </div>
            <h2 className="text-lg font-extrabold text-[#171426]">Join our Vittyyo Community</h2>
            <p className="text-sm text-[#6b6880] mt-2">Please join our Vittyyo community to download content — it's free to sign up.</p>
            <div className="mt-4 flex flex-col gap-2">
              <button
                data-testid="join-prompt-signup"
                onClick={() => navigate("/login")}
                className="w-full font-extrabold text-white py-2.5 rounded-full transition-transform hover:scale-[1.01]"
                style={{ background: "#1e3aaa", border: "2px solid #16308c" }}
              >
                Join / Sign up
              </button>
              <button
                data-testid="join-prompt-cancel"
                onClick={() => setJoinOpen(false)}
                className="w-full font-bold text-[#6b6880] py-2 rounded-full hover:bg-[#f1f4ee] transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

      <CreatorUnlockModal open={unlockOpen} onClose={() => setUnlockOpen(false)} />
    </div>
  );
}
